import { useMemo, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { rank } from "../switcher";
import type { Principal } from "../sync/types";
import { GroupsTab } from "./Groups";
import { openDMWith } from "./ProfileCard";
import { Avatar, EmptyState, Page } from "./ui";
import { t, tc } from "../i18n";

type Show = "people" | "agents" | "groups";

// pageSize is how many of the directory are shown at first, and how many
// more each time: tens of thousands of people drawn at once take seconds.
const pageSize = 120;
const byName = new Intl.Collator();

// PeopleView is the workspace's directory: everyone and every agent, to
// find, see the profile of, and message, and the user groups.
export function PeopleView() {
  const session = useSession();
  const state = useLantern();
  const [q, setQ] = useState("");
  const [show, setShow] = useState<Show>("people");
  const [error, setError] = useState("");
  const [limit, setLimit] = useState(pageSize);

  const everyone = useMemo(() => {
    const out = { people: [] as Principal[], agents: [] as Principal[] };
    for (const p of session.directory.values()) {
      if (p.status !== "active") continue;
      if (p.kind === "user") out.people.push(p);
      else if (p.kind === "agent") out.agents.push(p);
    }
    for (const list of [out.people, out.agents]) list.sort((a, b) => byName.compare(a.display_name || a.handle, b.display_name || b.handle));
    return out;
  }, [session.directory, state.profileBumps]);
  const all = show === "agents" ? everyone.agents : everyone.people;
  const shown: Principal[] = q.trim()
    ? rank(
        all.map((p) => ({ key: p.id, label: p.display_name || p.handle, also: "@" + p.handle, p })),
        q,
        200,
      ).map((e) => e.p)
    : all.slice(0, limit);
  const pick = (s: Show) => {
    setShow(s);
    setLimit(pageSize);
  };

  return (
    <Page className="people-view" title={t("People")} description={t("Everyone in the workspace, the agents that work in it, and the groups you can mention at once.")}>
      <div className="people-tools">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={show === "groups" ? t("Find a group by name or @handle") : t("Find someone by name or @handle")} aria-label={show === "groups" ? t("Find groups") : t("Find people")} autoFocus />
        <div className="segmented" role="tablist">
          <button role="tab" aria-selected={show === "people"} className={show === "people" ? "active" : ""} onClick={() => pick("people")}>
            {t("People · {n}", { n: everyone.people.length })}
          </button>
          <button role="tab" aria-selected={show === "agents"} className={show === "agents" ? "active" : ""} onClick={() => pick("agents")}>
            {t("Agents · {n}", { n: everyone.agents.length })}
          </button>
          <button role="tab" aria-selected={show === "groups"} className={show === "groups" ? "active" : ""} onClick={() => pick("groups")}>
            {t("Groups · {n}", { n: state.groups.length })}
          </button>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {show === "groups" ? (
        <GroupsTab q={q} />
      ) : shown.length === 0 ? (
        <EmptyState icon="🔎" title={q.trim() ? t("Nobody matches “{q}”", { q: q.trim() }) : t("Nobody here yet")} />
      ) : (
        <ul className="people-grid">
          {shown.map((p) => {
            const status = session.statusOf(p.id);
            const presence = p.kind === "agent" ? undefined : (state.presence.get(p.id) ?? "offline");
            return (
              <li key={p.id} className="card person">
                <button className="person-main" data-profile={p.id} aria-label={t("{name}'s profile", { name: p.display_name || p.handle })}>
                  <Avatar id={p.id} name={p.display_name || p.handle} size={48} presence={presence} />
                  <span className="person-text">
                    <strong>{p.display_name || p.handle}</strong>
                    <span className="muted">
                      @{p.handle}
                      {p.title && ` · ${p.title}`}
                    </span>
                    {status && (
                      <span className="person-status">
                        {status.emoji} {status.text}
                      </span>
                    )}
                  </span>
                </button>
                {p.id !== state.me?.id && (
                  <button
                    className="small"
                    onClick={() =>
                      void openDMWith(session, p.id).catch((err) => setError(err instanceof Error ? err.message : String(err)))
                    }
                  >
                    {tc("action", "Message")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {show !== "groups" && !q.trim() && all.length > limit && (
        <div className="people-more">
          <button onClick={() => setLimit((n) => n + pageSize)}>{t("Show {n} more", { n: Math.min(pageSize, all.length - limit) })}</button>
        </div>
      )}
    </Page>
  );
}
